import React, { createContext, useContext, useEffect, useState } from 'react';
import { get } from 'api';

/**
 * App-wide settings loaded from the backend (/v1/settings).
 *
 * Values live in the `settings` table in the database, so they can be changed
 * without rebuilding/redeploying the frontend. We cache the last response in
 * localStorage and hydrate from it synchronously on first render, so there's no
 * flash of missing values on repeat visits; then we refresh from the network.
 */

const SettingsContext = createContext({});

const CACHE_KEY = 'app_settings';

const readCache = () => {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (err) {
    return {};
  }
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(readCache);

  useEffect(() => {
    let active = true;

    get('/settings')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (active && data && typeof data === 'object') {
          setSettings(data);
          try {
            window.localStorage.setItem(CACHE_KEY, JSON.stringify(data));
          } catch (err) {
            // localStorage may be unavailable (private mode) - ignore
          }
        }
      })
      .catch(() => {
        // keep cached values if the request fails
      });

    return () => {
      active = false;
    };
  }, []);

  return (
    <SettingsContext.Provider value={settings}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
